import noop from 'lodash/noop';
import type { Merge } from 'type-fest';
import { DEFAULT_PAGE_SIZE } from '@atlassian/jira-business-constants';
import type { HierarchyLevelType } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/types.tsx';
import {
	type Field,
	safeGetTransformedFieldValue,
} from '@atlassian/jira-business-fields/src/utils/index.tsx';
import { isGroupItem } from '@atlassian/jira-business-list-handlers/src/common/utils/hierarchy/index.tsx';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config';
import { createStore, createHook } from '@atlassian/react-sweet-state';
import {
	type InlineCreateType,
	createItemSkeleton,
	createRowForInlineCreate,
	createRowForShowMore,
	isItemAnInlineCreate,
} from '../../../common/utils/row-placeholder';
import type {
	TransformedParentIssue,
	ListViewIssuesReturn as QueryData,
	ListViewIssue,
} from '../../../services/get-list-view-issues/types';
import type { ListViewControllerData as HierarchyItem } from '../../list-view-issues/types';
import type { RowCreateState, RowCreatePosition } from '../../row-create';

export const EPIC_LEVEL_ISSUE_TYPE = 1;

export type ExpandedItems = {
	[issueId: string]: boolean;
};

export type ChildrenItems = {
	[issueId: string]: QueryData;
};

type HierarchyData = Merge<
	QueryData,
	{
		// adding extra fields to ListViewIssue type, e.g. `children`, rankIndex etc.
		data: HierarchyItem[];
	}
>;

export type IssuesMap = Map<string, HierarchyItem>;
export type IssueParentMap = Map<string, string>;

type State = {
	// it is used in BaseTable component as data prop
	hierarchyData: HierarchyData;
	// expanded hierarchy items
	expandedItems: ExpandedItems;
	// GraphQL query response of every expanded item
	childrenItems: ChildrenItems;
	// Map of issue items by issueId
	issuesMap: IssuesMap;
	// We reset items cache when `projectKey` changes
	projectKey?: string;
	// Map of childIssueId to its parentIssueId
	issueParentMap: IssueParentMap;
};

const createLoadingItems = () =>
	Array.from(Array(DEFAULT_PAGE_SIZE).keys()).map((i) =>
		createItemSkeleton({ issueId: String(i) }),
	);

const getInitialState = () => ({
	hierarchyData: {
		data: createLoadingItems(),
		loading: true,
		totalCount: 0,
		isLoadingNextPage: false,
		loadNextPage: noop,
	},
	expandedItems: {},
	childrenItems: {},
	issuesMap: new Map(),
	issueParentMap: new Map(),
});

export const initialState = getInitialState();

const findAnchorIndex = (issues: ListViewIssue[], anchorId: string | undefined) => {
	if (anchorId == null) {
		return undefined;
	}

	const index = issues.findIndex((issue) => String(issue.issueId) === anchorId);
	if (index === -1) {
		return undefined;
	}

	return index;
};

// Recursively creates hierarchy tree by iterating over the items and their children
const createHierarchy = ({
	item,
	childrenItems,
	issuesMap,
	extendItemsWithChildCreate,
	hierarchyLevels,
	showFlatList,
	issueParentMap,
}: {
	item: HierarchyItem;
	childrenItems: ChildrenItems;
	issuesMap: IssuesMap;
	extendItemsWithChildCreate: (item: HierarchyItem, children: HierarchyItem[]) => HierarchyItem;
	hierarchyLevels: Array<HierarchyLevelType>;
	showFlatList?: boolean;
	issueParentMap: IssueParentMap;
}): HierarchyItem => {
	const childQueryData = childrenItems[item.issueId] || {};
	const childrenData = Array.isArray(childQueryData.data) ? childQueryData.data : [];

	const childrenCount: number = item.childSummary?.totalCount ?? 0;

	const epicLevelIssueTypeIds = hierarchyLevels
		.filter((hierarchyLevel) => hierarchyLevel.level === EPIC_LEVEL_ISSUE_TYPE)
		.map((hierarchyLevel) => hierarchyLevel.id);

	const hasChildren =
		item.issuetype?.id && epicLevelIssueTypeIds.includes(item.issuetype.id)
			? true
			: childrenCount > 0;

	let children: HierarchyItem[] = [];
	if ((isGroupItem(item.issueId) || showFlatList !== true) && hasChildren) {
		const depth = (item.depth || 0) + 1;
		const skeletonItem = childQueryData.loading
			? [createItemSkeleton({ issueId: item.issueId })]
			: [];

		children = childrenData.length
			? childrenData.map((childItem, index) => {
					issueParentMap.set(`${childItem.issueId}`, `${item.issueId}`);
					return createHierarchy({
						item: {
							...childItem,
							rankIndex: index,
							depth,
							rowKey: `${childItem.id}-${depth}`,
						},
						childrenItems,
						issuesMap,
						hierarchyLevels,
						extendItemsWithChildCreate,
						issueParentMap,
					});
				})
			: skeletonItem;

		if (childQueryData.hasNextPage === true) {
			children.push(createRowForShowMore(item.issueId));
		}
	}

	const newItem = extendItemsWithChildCreate(item, children);

	issuesMap.set(`${item.issueId}`, newItem);

	return newItem;
};

const extendItemsWithChildCreate =
	(
		rowCreateState: RowCreateState,
		setRowCreatePosition: (rowCreatePosition: RowCreatePosition) => void,
		getState: () => State,
		setState: (arg1: State) => void,
		hierarchyLevels: Array<HierarchyLevelType>,
	) =>
	(item: HierarchyItem, children: HierarchyItem[]) => {
		const expandItem = (issueId: string) => {
			const state = getState();
			if (!state.expandedItems[issueId]) {
				setState({
					...state,
					expandedItems: { ...state.expandedItems, [issueId]: true },
				});
			}
		};

		const insertInlineCreate = (
			anchorIndex: number | undefined,
			type: InlineCreateType,
			issueId: number,
		) => {
			if (anchorIndex != null) {
				children.splice(anchorIndex, 0, createRowForInlineCreate(type, String(issueId)));
			} else {
				children.unshift(createRowForInlineCreate(type, String(issueId)));
			}
		};

		const hierarchyLevel = hierarchyLevels.find((x) => x.id === item.issuetype?.id)?.level;

		const anchorIndex = findAnchorIndex(children, rowCreateState.anchorId);

		const { parentId } = rowCreateState;
		if (String(parentId) === String(item.issueId)) {
			if (isGroupItem(item.issueId)) {
				insertInlineCreate(anchorIndex, 'group', item.issueId);
			} else if (hierarchyLevel === 0) {
				insertInlineCreate(anchorIndex, 'subtask', item.issueId);
			} else if (hierarchyLevel && hierarchyLevel >= 1) {
				insertInlineCreate(anchorIndex, 'task', item.issueId);
			}
		}

		return {
			...item,
			children,
			onChildCreateButtonClicked:
				hierarchyLevel !== -1
					? () => {
							setRowCreatePosition({
								parentId: item.issueId,
							});
							expandItem(item.issueId);
						}
					: undefined,
		};
	};

const transformTopLevelIssues = (
	data: ListViewIssue[] | HierarchyItem[],
	rowCreateState: RowCreateState,
) => {
	const { parentId, anchorId } = rowCreateState;
	/**
	 * Due to race conditions, we cannot trust query data to be the source of truth for top level issues,
	 * so we need to make sure any inline create row has been removed before proceeding
	 */
	const topLevelIssues = data.filter((item) => !isItemAnInlineCreate(String(item.issueId)));

	if (anchorId && !parentId) {
		const anchorIndex = findAnchorIndex(topLevelIssues, rowCreateState.anchorId);
		if (anchorIndex != null) {
			// Insert the sibling issue create row
			topLevelIssues.splice(anchorIndex, 0, createRowForInlineCreate('sibling'));
		}
	}

	return topLevelIssues;
};

// Takes top level items (or Groups) GraphQL response and stores it in here
const setHierarchyData =
	(
		queryData: QueryData,
		rowCreateState: RowCreateState,
		setRowCreatePosition: (rowCreatePosition: RowCreatePosition) => void,
		hierarchyLevels: HierarchyLevelType[],
		showFlatList?: boolean,
	) =>
	({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
		const state = getState();
		const { childrenItems, issuesMap, issueParentMap } = state;

		const childCreateCallback = extendItemsWithChildCreate(
			rowCreateState,
			setRowCreatePosition,
			getState,
			setState,
			hierarchyLevels,
		);

		if (queryData.loading) {
			return;
		}

		/**
		 * transformTopLevelIssues is used to transform both ListViewIssue[] and HierarchyItem[] array types, depending on what is passed as the 'data' argument,
		 *  hence the need to type cast it as ListViewIssue[] to avoid type errors
		 */
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const topLevelIssues = transformTopLevelIssues(
			queryData.data,
			rowCreateState,
		) as ListViewIssue[];

		const data = queryData.loading
			? createLoadingItems()
			: topLevelIssues.map((item, index) =>
					createHierarchy({
						item: {
							...item,
							rankIndex: index,
							depth: 0,
							rowKey: `${item.id}-0`,
						},
						childrenItems,
						issuesMap,
						extendItemsWithChildCreate: childCreateCallback,
						showFlatList,
						hierarchyLevels,
						issueParentMap,
					}),
				);

		setState({ ...state, hierarchyData: { ...queryData, data } });
	};

// Updates expanded items
const setExpandedItems =
	(issueId: string, isExpanded: boolean) =>
	({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
		const state = getState();
		setState({
			...state,
			expandedItems: { ...state.expandedItems, [issueId]: isExpanded },
		});
	};

// Stores GraphQL response of expanded child items
const setChildrenItems =
	(
		parentIssueId: string,
		queryData: QueryData,
		rowCreateState: RowCreateState,
		setRowCreatePosition: (rowCreatePosition: RowCreatePosition) => void,
		hierarchyLevels: {
			level: number;
			name: string;
			id: string;
		}[],
	) =>
	({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
		const state = getState();
		const childrenItems = {
			...state.childrenItems,
			[parentIssueId]: {
				...queryData,
				...(queryData.data
					? {
							data: queryData.data.map((issue) => ({
								...issue,
								parentIssueId,
							})),
						}
					: {}),
			},
		};
		const { issuesMap, issueParentMap } = state;

		let targetFound = false;

		const updateHierarchy = ({ rootIssueId }: { rootIssueId: string }) => {
			const item = issuesMap.get(`${rootIssueId}`);
			// We can stop traversal once the parent node has been updated
			if (targetFound || !item) {
				return null;
			}

			const childCreateCallback = extendItemsWithChildCreate(
				rowCreateState,
				setRowCreatePosition,
				getState,
				setState,
				hierarchyLevels,
			);

			if (String(item.issueId) === String(parentIssueId)) {
				// If we find the node we're appending children to, we need to create the new hierarchy below it and return to notify parents to update
				targetFound = true;
				return createHierarchy({
					item,
					childrenItems,
					issuesMap,
					extendItemsWithChildCreate: childCreateCallback,
					hierarchyLevels,
					issueParentMap,
				});
			}

			const { children } = item;
			const updatedChild = children?.reduce<HierarchyItem | undefined>((acc, curr, index) => {
				const newChild = updateHierarchy({
					rootIssueId: curr.issueId,
				});

				if (newChild) {
					children[index] = newChild;
				}

				return newChild ?? acc;
			}, undefined);

			if (updatedChild) {
				// if one of the children updated, then we need to update this node as well
				const newItem = {
					...item,
					children,
				};

				issuesMap.set(`${item.issueId}`, newItem);

				return newItem;
			}

			return null;
		};

		const topLevelItems = state.hierarchyData.data;

		for (let index = 0, count = topLevelItems.length; index < count; index += 1) {
			const item = topLevelItems[index];
			const newItem = updateHierarchy({ rootIssueId: item.issueId });

			if (newItem) {
				topLevelItems[index] = newItem;
			}

			if (targetFound) {
				// the root node containing the parent has been found, early exit the remaining items
				break;
			}
		}

		setState({
			...state,
			childrenItems,
			hierarchyData: { ...state.hierarchyData, data: [...topLevelItems] },
		});
	};

// rebuilds the hierarchy, factoring in changes to row create position
const refreshHierarchy =
	(
		rowCreateState: RowCreateState,
		setRowCreatePosition: (rowCreatePosition: RowCreatePosition) => void,
		hierarchyLevels: {
			level: number;
			name: string;
			id: string;
		}[],
	) =>
	({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
		const state = getState();
		const { issuesMap, childrenItems, issueParentMap } = state;

		/**
		 * getTopLevelIssues is used to transform both ListViewIssue[] and HierarchyItem[], depending on what is passed as the 'data' argument,
		 *  hence the need to type cast it as HierarchyItem[] to avoid type errors
		 */
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const topLevelIssues = transformTopLevelIssues(
			state.hierarchyData.data,
			rowCreateState,
		) as HierarchyItem[];

		for (let index = 0, count = topLevelIssues.length; index < count; index += 1) {
			const item = topLevelIssues[index];

			const childCreateCallback = extendItemsWithChildCreate(
				rowCreateState,
				setRowCreatePosition,
				getState,
				setState,
				hierarchyLevels,
			);

			const newItem = createHierarchy({
				item,
				childrenItems,
				issuesMap,
				extendItemsWithChildCreate: childCreateCallback,
				hierarchyLevels,
				issueParentMap,
			});

			if (newItem) {
				topLevelIssues[index] = newItem;
			}
		}

		setState({
			...state,
			childrenItems,
			hierarchyData: { ...state.hierarchyData, data: [...topLevelIssues] },
		});
	};

const resetCache =
	() =>
	({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
		const state = getState();

		setState({
			...state,
			childrenItems: {},
			expandedItems: {},
		});
	};

const expandFirstItems =
	() =>
	({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
		const state = getState();
		const firstIssueId = state.hierarchyData.data[0]?.issueId;

		setState({
			...state,
			expandedItems: firstIssueId != null ? { [firstIssueId]: true } : {},
		});
	};

const clearExpandedItems =
	() =>
	({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
		const state = getState();
		setState({
			...state,
			expandedItems: Object.fromEntries(
				Object.keys(state.expandedItems).map((key) => [key, false]),
			),
		});
	};

const getParentIssueKeysByChildId =
	(issueId: string) =>
	({ getState }: { getState: () => State }): { key: string; id: string }[] | null => {
		const { issuesMap } = getState();
		let parents: { id: string; key: string }[] = [];

		let node = issuesMap.get(`${issueId}`);
		do {
			const parentIssue = safeGetTransformedFieldValue<TransformedParentIssue | null>(
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				node?.fields as Field[],
				PARENT_TYPE,
				'issue',
				null,
			);
			const cachedParentIssueId = !isGroupItem(`${node?.parentIssueId}`)
				? node?.parentIssueId
				: null;
			const parentIssueId = parentIssue?.issueId ? parentIssue?.issueId : cachedParentIssueId;

			if (parentIssueId) {
				node = issuesMap.get(`${parentIssueId}`);
				// When parent issue is cached in hierarchy store
				if (node) {
					parents = [{ id: `${parentIssueId}`, key: `${node.key}` }, ...parents];
				}
				// When we load List view with filter applied via URL, hierarchy store might not have parent issues cached
				// Hence we take parentField from BE response. Note that each issue will "know" only closest parent
				else if (parentIssue?.issueKey) {
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const key = `${(parentIssue as TransformedParentIssue)?.issueKey || ''}`;
					parents = [
						{
							id: `${parentIssueId}`,
							key,
						},
						...parents,
					];
				}
			} else {
				node = undefined;
			}
		} while (node);

		if (parents.length) {
			return parents;
		}

		return null;
	};

const clearHierarchyData =
	() =>
	({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
		const state = getState();
		setState({
			...state,
			hierarchyData: getInitialState().hierarchyData,
		});
	};

const initState =
	({ projectKey }: { projectKey: string }) =>
	({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
		const state = getState();
		setState({
			...state,
			projectKey,
		});
	};

const actions = {
	setHierarchyData,
	setExpandedItems,
	setChildrenItems,
	refreshHierarchy,
	expandFirstItems,
	clearExpandedItems,
	clearHierarchyData,
	resetCache,
	getParentIssueKeysByChildId,
	initState,
} as const;

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	initialState,
	actions,
	name: 'JWMHierarchyStore',
});

export const useHierarchyStore = createHook(Store);
